import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { devLog } from "../lib/devLog";
import { Copy, Plus, Trash, X, LinkSimple } from "@phosphor-icons/react";
import { ConfirmDialog } from "./ConfirmDialog";
import { ROLE_OPTIONS, roleLabel } from "../lib/labels";

const ROLES = ROLE_OPTIONS.map((r) => r.value);

export function TeamTab({ project, members, isProjectAdmin, onUpdate }) {
  const [invites, setInvites] = useState([]);
  const [showInvite, setShowInvite] = useState(false);
  const [pendingRemoveId, setPendingRemoveId] = useState(null);

  const loadInvites = async () => {
    if (!isProjectAdmin) return;
    try {
      const { data } = await api.get(`/projects/${project.id}/invites`);
      setInvites(data);
    } catch (e) {
      devLog.error("[TeamTab] loadInvites failed", e);
    }
  };

  useEffect(() => { loadInvites(); }, [project.id, isProjectAdmin]);

  const updateRole = async (userId, role) => {
    try {
      await api.put(`/projects/${project.id}/members/${userId}/role`, { role });
      onUpdate();
    } catch (e) {
      alert(formatApiError(e.response?.data?.detail));
    }
  };

  const removeMember = async (userId) => {
    try {
      await api.delete(`/projects/${project.id}/members/${userId}`);
      onUpdate();
    } catch (e) {
      alert(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-8" data-testid="team-tab">
      <div className="flex justify-between items-end">
        <div>
          <p className="label-mono mb-1">Roster</p>
          <h2 className="font-heading text-2xl font-bold tracking-tight">Team members</h2>
        </div>
        {isProjectAdmin && (
          <button data-testid="open-invite-modal" onClick={() => setShowInvite(true)} className="btn-primary">
            <Plus size={16} weight="bold" /> Invite member
          </button>
        )}
      </div>

      <div className="surface rounded-md overflow-hidden">
        <div className="grid grid-cols-12 px-6 py-3 border-b border-[color:var(--border)] bg-[color:var(--surface-2)]">
          <div className="col-span-5 label-mono">Operator</div>
          <div className="col-span-3 label-mono">Role</div>
          <div className="col-span-4 label-mono text-right">Actions</div>
        </div>
        {members.map((m) => {
          const isOwner = m.id === project.admin_id;
          return (
            <div key={m.id} className="grid grid-cols-12 px-6 py-4 border-b border-[color:var(--border)] last:border-0 items-center" data-testid={`member-row-${m.id}`}>
              <div className="col-span-5">
                <p className="font-semibold">{m.name} {isOwner && <span className="text-xs text-[color:var(--text-tertiary)] ml-1">(owner)</span>}</p>
                <p className="text-xs text-[color:var(--text-secondary)] font-mono">{m.email}</p>
              </div>
              <div className="col-span-3">
                {isProjectAdmin && !isOwner ? (
                  <select
                    data-testid={`role-select-${m.id}`}
                    value={m.role}
                    onChange={(e) => updateRole(m.id, e.target.value)}
                    className="select-tactical max-w-[180px]"
                  >
                    {ROLE_OPTIONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                  </select>
                ) : (
                  <span className={`role-badge role-${m.role}`}>{roleLabel(m.role)}</span>
                )}
              </div>
              <div className="col-span-4 text-right">
                {isProjectAdmin && !isOwner && (
                  <button data-testid={`remove-member-${m.id}`} onClick={() => setPendingRemoveId(m.id)} className="btn-danger">
                    <Trash size={12} /> Remove
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {isProjectAdmin && invites.length > 0 && (
        <div>
          <p className="label-mono mb-3">Active invites</p>
          <div className="space-y-2">
            {invites.filter((i) => !i.used).map((inv) => (
              <InviteRow key={inv.id} invite={inv} />
            ))}
            {invites.filter((i) => !i.used).length === 0 && (
              <p className="text-sm text-[color:var(--text-tertiary)]">No pending invites.</p>
            )}
          </div>
        </div>
      )}

      {showInvite && (
        <InviteModal
          projectId={project.id}
          onClose={() => setShowInvite(false)}
          onCreated={() => { setShowInvite(false); loadInvites(); }}
        />
      )}
      <ConfirmDialog
        open={!!pendingRemoveId}
        title="Remove member?"
        message="They will lose access to this project. They can be re-invited later."
        confirmLabel="Remove member"
        onCancel={() => setPendingRemoveId(null)}
        onConfirm={async () => { const id = pendingRemoveId; setPendingRemoveId(null); await removeMember(id); }}
      />
    </div>
  );
}

function InviteRow({ invite }) {
  const url = `${window.location.origin}/invite/${invite.token}`;
  const [copied, setCopied] = useState(false);

  const copy = () => {
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="surface-2 rounded-md p-4 flex items-center justify-between gap-4 flex-wrap" data-testid={`invite-row-${invite.id}`}>
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <LinkSimple size={18} className="text-[#007AFF] shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-mono truncate">{url}</p>
          <p className="text-xs text-[color:var(--text-secondary)] mt-0.5">
            For: {invite.email || "anyone"} · Role: <span className={`role-badge role-${invite.role} ml-1`}>{roleLabel(invite.role)}</span>
          </p>
        </div>
      </div>
      <button data-testid={`copy-invite-${invite.id}`} onClick={copy} className="btn-ghost">
        <Copy size={14} /> {copied ? "Copied!" : "Copy link"}
      </button>
    </div>
  );
}

function InviteModal({ projectId, onClose, onCreated }) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("worker");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [createdLink, setCreatedLink] = useState(null);
  const [copied, setCopied] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const { data } = await api.post(`/projects/${projectId}/invites`, {
        email: email || null,
        role,
      });
      const url = `${window.location.origin}/invite/${data.token}`;
      setCreatedLink(url);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    }
    setSaving(false);
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-6 border-b border-[color:var(--border)]">
          <div>
            <p className="label-mono">Recruit</p>
            <h2 className="font-heading text-xl font-bold tracking-tight mt-1">Invite a member</h2>
          </div>
          <button onClick={onClose} className="text-[color:var(--text-secondary)] hover:text-white"><X size={20} /></button>
        </div>
        {createdLink ? (
          <div className="p-6 space-y-4">
            <p className="text-sm text-[color:var(--text-secondary)]">
              Share this link with the person you want to invite. Once they accept, they'll join the project.
            </p>
            <div className="surface-2 rounded-md p-4">
              <p className="text-sm font-mono break-all">{createdLink}</p>
            </div>
            <button
              data-testid="copy-generated-invite-link"
              onClick={() => { navigator.clipboard.writeText(createdLink); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
              className="btn-primary w-full justify-center"
            >
              <Copy size={14} /> {copied ? "Copied!" : "Copy invite link"}
            </button>
            <button onClick={onCreated} className="btn-ghost w-full justify-center">Done</button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-6 space-y-4">
            <div>
              <label className="label-mono block mb-2">Email (optional)</label>
              <input data-testid="invite-email-input" type="email" className="input" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="teammate@example.com" />
              <p className="text-xs text-[color:var(--text-tertiary)] mt-2">Leave blank for an open link any signed-in user can use.</p>
            </div>
            <div>
              <label className="label-mono block mb-2">Assign role</label>
              <select data-testid="invite-role-select" className="select-tactical" value={role} onChange={(e) => setRole(e.target.value)}>
                {ROLE_OPTIONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </div>
            {error && <div className="text-sm text-red-400">{error}</div>}
            <button data-testid="generate-invite-button" disabled={saving} className="btn-primary w-full justify-center">
              {saving ? "Generating..." : "Generate invite link"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
